import { useEffect, useState } from 'react';
import { Box, Button, Divider, Skeleton, Typography } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import GitHubIcon from '@mui/icons-material/GitHub';
import AppleIcon from '@mui/icons-material/Apple';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@services/api';

// Microsoft icon is not in MUI, so we create a simple one
const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" fill="currentColor">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

interface OAuthProvider {
  id: string;
  name: string;
  enabled: boolean;
}

interface OAuthProvidersResponse {
  success: boolean;
  data: {
    providers: string[];
  };
  requestId?: string;
}

interface OAuthButtonsProps {
  mode: 'login' | 'register';
}

const providerIcons: Record<string, React.ReactNode> = {
  google: <GoogleIcon />,
  github: <GitHubIcon />,
  microsoft: <MicrosoftIcon />,
  apple: <AppleIcon />,
};

const providerColors: Record<string, { bg: string; hover: string; text: string }> = {
  google: { bg: '#fff', hover: '#f5f5f5', text: '#757575' },
  github: { bg: '#24292e', hover: '#2f363d', text: '#fff' },
  microsoft: { bg: '#fff', hover: '#f5f5f5', text: '#5e5e5e' },
  apple: { bg: '#000', hover: '#333', text: '#fff' },
};

function OAuthButtons({ mode }: OAuthButtonsProps) {
  const { t } = useTranslation();
  const [providers, setProviders] = useState<OAuthProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await apiClient.get<OAuthProvidersResponse>('/v1/auth/providers');
        // API returns array of strings, transform to OAuthProvider objects
        const enabledProviders = response.data.providers.map((id: string) => ({
          id,
          name: id.charAt(0).toUpperCase() + id.slice(1),
          enabled: true,
        }));
        setProviders(enabledProviders);
      } catch {
        setError('Failed to load sign-in options');
      } finally {
        setIsLoading(false);
      }
    };

    void fetchProviders();
  }, []);

  const handleOAuthClick = (providerId: string) => {
    // Redirect to the OAuth endpoint - the API will handle the OAuth flow
    const apiUrl = import.meta.env.VITE_API_URL || '';
    window.location.href = `${apiUrl}/v1/auth/${providerId}`;
  };

  // Don't show anything if loading or no providers available
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Skeleton variant="rounded" height={42} />
        <Skeleton variant="rounded" height={42} />
      </Box>
    );
  }

  if (error || providers.length === 0) {
    return null;
  }

  const actionText = mode === 'login' ? t('auth.oauth.signInWith') : t('auth.oauth.signUpWith');

  // REV-677: the divider is rendered HERE, not by the page, because this
  // component is the only thing that knows whether anything is about to be
  // shown. Both auth pages used to bracket it with their own dividers, so on
  // the common configuration — no OAuth providers, this component returning
  // null — the user saw two adjacent rules with an empty gap between them.
  return (
    <>
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
          {mode === 'login' ? t('auth.oauth.orSignInWith') : t('auth.oauth.orSignUpWith')}
        </Typography>
        {providers.map((provider) => {
          const colors = providerColors[provider.id] || {
            bg: '#fff',
            hover: '#f5f5f5',
            text: '#333',
          };
          return (
            <Button
              key={provider.id}
              variant="outlined"
              fullWidth
              startIcon={providerIcons[provider.id]}
              onClick={() => handleOAuthClick(provider.id)}
              sx={{
                backgroundColor: colors.bg,
                color: colors.text,
                borderColor: colors.bg === '#fff' ? '#ddd' : colors.bg,
                textTransform: 'none',
                fontWeight: 500,
                py: 1.25,
                '&:hover': {
                  backgroundColor: colors.hover,
                  borderColor: colors.bg === '#fff' ? '#ccc' : colors.hover,
                },
              }}
            >
              {actionText} {provider.name}
            </Button>
          );
        })}
      </Box>
    </>
  );
}

export default OAuthButtons;
